import { useMutation } from '@apollo/client';
import { Body, Button, LargeTitle, LegalBody } from '@indigo-ag/component-module';
import {
  AgFinancingCollateralAnalysisStatus,
  AgFinancingFile,
  AgFinancingInput,
  CollateralInput,
  CollateralType,
  Phase2,
  Phase2Input
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { ExecutionResult } from 'graphql';
import React, { useContext, useState } from 'react';

import { OrderDetail } from 'features/brazil-grower/components/BrazilGrowerFinance/components/OrderDetail/OrderDetail';
import { FieldMonitoringSection } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/FieldMonitoringSection/FieldMonitoringSection';
import { PhaseWrapper } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseWrapper';
import { SendAnalysisModal } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/SendAnalysisModal/SendAnalysisModal';
import { ToggleOnHoldCollateralAnalysis } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/ToggleOnHoldCollateralAnalysis/ToggleOnHoldCollateralAnalysis';
import { WarrantyType } from 'features/brazil-grower/components/WarrantyType/WarrantyType';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import {
  CollateralUtil,
  getAnalysisData,
  tooltipHelperForDisabledElements,
  utils,
  WithConditionalTooltip
} from 'shared';
import { MatriculaAnalysisSection } from '../../sections/Analysis/MatriculaAnalisysSection';
import { PricingTable } from '../../sections/PricingTable/PricingTable';
import { CollateralAnalysisStatusTag } from '../CollateralAnalysisStatusTag/CollateralAnalysisStatusTag';

/**
 * TODO Revisit this update logic and try to make it dynamic
 */
interface ICollateralAnalysisContext {
  updateCollateral: (
    newCollateralInfo: Partial<CollateralInput>,
    files?: AgFinancingFile[]
  ) => Promise<ExecutionResult<any>>;
  updatePhase2: (newPhase2Info: Partial<Phase2Input>) => Promise<ExecutionResult<any>>;
}

export const CollateralAnalysisContext = React.createContext<ICollateralAnalysisContext>({
  updateCollateral: () => Promise.resolve({}),
  updatePhase2: () => Promise.resolve({})
});

interface CollateralAnalysisProps {
  collateral: Phase2['collateral'];
  fieldMonitoring: Phase2['fieldMonitoring'];
  matriculas: Phase2['matriculas'];
  phase2Files: Phase2['files'];
}

export const CollateralAnalysis: React.FunctionComponent<CollateralAnalysisProps> = ({
  collateral,
  matriculas,
  fieldMonitoring
}) => {
  /**
   * Finance Process Context
   */
  const { agFinancingId, orderInfo, collateralType, agFinance } = useContext(FinanceProcessContext);

  /**
   * Mutations
   */
  const [upsertFinancingAgFinancingFn] = useMutation(upsertFinancingAgFinancing);

  /**
   * State
   */
  const [sendAnalysisModalOpen, setSendAnalysisModalOpen] = useState(false);

  const closeSendAnalysisModal = () => {
    setSendAnalysisModalOpen(false);
  };

  const handleSendAnalysis = () => {
    setSendAnalysisModalOpen(true);
  };

  const upsertFinancingAgFinancingFnWrapper = (agFinancingData: Partial<AgFinancingInput>) => {
    const newAgFinancingData: Partial<AgFinancingInput> = {
      id: agFinancingId,
      ...agFinancingData
    };

    return upsertFinancingAgFinancingFn({
      variables: {
        data: newAgFinancingData
      }
    });
  };

  const updateCollateral = (newCollateralInfo: Partial<CollateralInput>, files?: AgFinancingFile[]) => {
    return upsertFinancingAgFinancingFnWrapper({
      phase2: {
        collateral: {
          ...newCollateralInfo
        },
        files
      }
    });
  };

  const sendAnalysis = (notes: string) => {
    return updateCollateral({ status: AgFinancingCollateralAnalysisStatus.COMPLETED, notes });
  };

  const updatePhase2 = (newPhase2Info: Partial<Phase2Input>) => {
    return upsertFinancingAgFinancingFnWrapper({
      phase2: {
        ...newPhase2Info
      }
    });
  };

  const analysisNotSent = CollateralUtil.hasNotBeenSent(collateral?.status);
  const isPromissoryNote = collateralType === CollateralType.PROMISSORY_NOTE;
  const shouldDisableApprovalBtn =
    !isPromissoryNote && !getAnalysisData(matriculas)?.matriculas?.filter(m => m.includedInAnalysis).length;
  const isInternalCampaign = agFinance?.campaign?.isExternal === false;

  return !!collateral ? (
    <CollateralAnalysisContext.Provider
      value={{
        updateCollateral,
        updatePhase2
      }}
    >
      <div className="flex">
        <PhaseWrapper
          title={
            <div className="flex">
              <LargeTitle className="mr-3">{i18next.t('collateralAnalysis')}</LargeTitle>
              <CollateralAnalysisStatusTag status={collateral?.status} />
            </div>
          }
          actionElement={
            <>
              <ToggleOnHoldCollateralAnalysis />
              {analysisNotSent && (
                <>
                  <WithConditionalTooltip
                    visible={shouldDisableApprovalBtn}
                    title={
                      <LegalBody>{utils.text.firstLetterUppercase(i18next.t('includeMatriculasMessage'))}</LegalBody>
                    }
                    placement="top"
                  >
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={handleSendAnalysis}
                      disabled={shouldDisableApprovalBtn}
                      style={tooltipHelperForDisabledElements(shouldDisableApprovalBtn)}
                    >
                      {utils.text.toTitleCase(i18next.t('markComplete'))}
                    </Button>
                  </WithConditionalTooltip>
                  <SendAnalysisModal
                    closeModal={closeSendAnalysisModal}
                    open={sendAnalysisModalOpen}
                    message={i18next.t('askForApprovalLuchesiMessage')}
                    onConfirmAction={sendAnalysis}
                  />
                </>
              )}
            </>
          }
          subtitleElement={<WarrantyType collateralType={collateralType} />}
        >
          {!isPromissoryNote && <MatriculaAnalysisSection analysisInfo={getAnalysisData(matriculas)} />}
          {isInternalCampaign && <PricingTable />}
          <FieldMonitoringSection fieldMonitoringInfo={fieldMonitoring} />
        </PhaseWrapper>
        <OrderDetail orderInfo={orderInfo} />
      </div>
    </CollateralAnalysisContext.Provider>
  ) : (
    <Body>{utils.text.toTitleCase(i18next.t('thisProcessHasNotStartedYet'))}</Body>
  );
};
